import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiErrorMessage } from '../../utils/apiError';
import mcpServiceService from '../../services/mcpServiceService';
import Button from '../shared/Button';
import ConfirmModal from '../shared/ConfirmModal';

// MCP AS A SERVICE — TERMS, per hotel (V641, doc/MCP-AS-A-SERVICE.md §9.2, FR-001).
//
//   Service agreement   the agreement with hconnect that applies to this hotel — its own approved version, else
//                       hconnect's default. The service answers no assistant until the hotel's admin accepts exactly
//                       that version. A hotel may propose its own text; hconnect approves it (it is the other party),
//                       and the approved version must then be accepted.
//   Guest booking terms the hotel's own terms for guests: each save is a new version, current at once. Assistants show
//                       them at quote time and must confirm the guest accepted that version to book.
//   (superadmin)        hconnect's default agreement and the proposals waiting for approval are platform-wide, so they
//                       are on Admin › Platform › MCP as a Service (McpAdminPage / McpDefaultAgreement); a link here.
//
// Versions are never edited: what was accepted can always be shown as it was.

const when = (v) => (v ? new Date(v).toLocaleString() : '—');

export default function McpTermsTab({ hotels }) {
  const [hotelId, setHotelId] = useState(hotels[0] ? hotels[0].hotelId : null);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [editor, setEditor] = useState(null);   // { kind: 'agreement' | 'guest', title, body }
  const [confirmAccept, setConfirmAccept] = useState(false);

  const load = useCallback(async () => {
    if (!hotelId) return;
    setError(null);
    try {
      setData(await mcpServiceService.getTerms(hotelId));
    } catch (e) { setError(apiErrorMessage(e)); }
  }, [hotelId]);
  useEffect(() => { load(); }, [load]);

  const act = async (what, fn) => {
    setBusy(what); setError(null);
    try { await fn(); await load(); } catch (e) { setError(apiErrorMessage(e)); } finally { setBusy(null); }
  };

  const saveEditor = () => {
    const e = editor;
    setEditor(null);
    if (e.kind === 'agreement') return act('propose', () => mcpServiceService.proposeAgreement(hotelId, { title: e.title, body: e.body }));
    return act('guest', () => mcpServiceService.saveGuestTerms(hotelId, { title: e.title, body: e.body }));
  };

  const a = data && data.agreement;
  const accepted = !!(data && data.accepted);

  return (
    <div className="mcps-section">
      {/* One hotel's page passes just that hotel — no picker then. */}
      {hotels.length > 1 && <div className="mcps-grid">
        <label>Hotel<select value={hotelId || ''} onChange={e => setHotelId(e.target.value)}>
          {hotels.map(h => <option key={h.hotelId} value={h.hotelId}>{h.hotelName}</option>)}</select></label>
      </div>}
      {error && <div className="os-msg os-msg-error">{error}</div>}

      {data && (
        <>
          <div className="mcps-card">
            <div className="mcps-card-head">
              <h3>Service agreement with hconnect</h3>
              {a && <span className={`mcps-pill ${accepted ? 'mcps-pill-verified' : 'mcps-pill-pending'}`}>{accepted ? 'Accepted' : 'Not accepted'}</span>}
            </div>
            {!a ? <p className="mcps-meta">hconnect has not published the agreement yet. The service cannot start until it has, and you accept it.</p> : (
              <>
                <div className="mcps-meta">{a.title} · version {a.version}{a.custom ? ' · this hotel’s own version' : ' · hconnect’s standard agreement'} · published {when(a.published_at)}</div>
                <div className="mcps-terms-text">{a.body}</div>
                {accepted
                  ? <div className="mcps-meta">Accepted {when(data.accepted.accepted_at)} by {data.accepted.accepted_by}.</div>
                  : <div className="mcps-terms">Assistants cannot use this hotel until this version is accepted.
                      {data.canEdit && <Button variant="primary" disabled={!!busy} onClick={() => setConfirmAccept(true)}>Accept this agreement</Button>}</div>}
              </>
            )}
            {data.proposal && <div className="os-msg">Your proposed version ({when(data.proposal.created_at)}, by {data.proposal.created_by}) is waiting for hconnect&rsquo;s approval.</div>}
            {data.canEdit && a && <div className="mcps-actions">
              <Button variant="secondary" disabled={!!busy} onClick={() => setEditor({ kind: 'agreement', title: a.title,
                body: data.proposal ? data.proposal.body : a.body })}>Propose changes</Button></div>}
          </div>

          <div className="mcps-card">
            <div className="mcps-card-head"><h3>Guest booking terms</h3>
              {data.guest && <span className="mcps-meta">version {data.guest.version} · {when(data.guest.published_at)}</span>}</div>
            {data.guest ? <div className="mcps-terms-text">{data.guest.body}</div>
              : <p className="mcps-meta">None set. Assistants will book under the rate&rsquo;s own conditions only.</p>}
            <p className="mcps-meta">Assistants show these at quote time and must confirm the guest accepted this version before booking. The
              confirmation email includes them. Saving makes a new version straight away; past bookings keep the version they accepted.</p>
            {data.canEdit && <Button variant="secondary" disabled={!!busy} onClick={() => setEditor({ kind: 'guest',
              title: data.guest ? data.guest.title : 'Booking terms and conditions', body: data.guest ? data.guest.body : '' })}>
              {data.guest ? 'Edit (new version)' : 'Write guest terms'}</Button>}
            {data.guestHistory.length > 1 && <table className="mcps-table">
              <thead><tr><th>Version</th><th>Status</th><th>Published</th><th>By</th></tr></thead>
              <tbody>{data.guestHistory.map(h => <tr key={h.id}><td>{h.version}</td><td>{h.status}</td><td>{when(h.published_at)}</td><td>{h.created_by}</td></tr>)}</tbody>
            </table>}
          </div>

          {data.isSuperAdmin && (
            <div className="os-msg">hconnect&rsquo;s default agreement, and approving hotels&rsquo; proposals, are on{' '}
              <Link to="/admin/mcp-service?tab=default">Admin › Platform › MCP as a Service</Link>.</div>
          )}
        </>
      )}

      {editor && (
        <ConfirmModal wide closeOnBackdrop={false}
                      title={editor.kind === 'agreement' ? 'Propose this hotel’s agreement' : 'Guest booking terms'}
                      confirmLabel={editor.kind === 'agreement' ? 'Send to hconnect for approval' : 'Publish as new version'}
                      confirmDisabled={!editor.title.trim() || !editor.body.trim()}
                      onCancel={() => setEditor(null)} onConfirm={saveEditor}>
          <label className="mcps-wide">Title<input value={editor.title} maxLength={200} onChange={e => setEditor(x => ({ ...x, title: e.target.value }))} /></label>
          <label className="mcps-wide">Text<textarea rows={18} value={editor.body} onChange={e => setEditor(x => ({ ...x, body: e.target.value }))} /></label>
        </ConfirmModal>
      )}

      {confirmAccept && a && (
        <ConfirmModal title={`Accept ${a.title} (version ${a.version})`} confirmLabel="I accept on behalf of the hotel"
                      onCancel={() => setConfirmAccept(false)}
                      onConfirm={() => { setConfirmAccept(false); act('accept', () => mcpServiceService.acceptAgreement(hotelId, a.id)); }}>
          <p className="cm-message">You are accepting the agreement shown, version {a.version}, for {hotels.find(h => String(h.hotelId) === String(hotelId))?.hotelName}.
            Your name and the time are recorded.</p>
        </ConfirmModal>
      )}
    </div>
  );
}
